import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import { Button, Container, Grid, IconButton, Paper, Stack, Typography } from '@mui/material';

import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';

import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ActivityEnum, AdminSubactivityEnum, useUserActivityTracker } from '../UserActivityTracker';
import { DialogPlanApi, IDialogPlan } from '../api/DialogPlanApi';
import { DialogPlanLlmPrompt, DialogPlanLlmPromptApi } from '../api/DialogPlanLlmPromptApi';
import { UserApi } from '../api/UserApi';
import { handleErrorMessage, useErrorDialog } from '../component/ErrorDialog';
import DialogPlanForm from '../component/dialogPlan/DialogPlanForm';
import DialogPlanLlmPromptForm, { getClassNameDescription as getDialogPlanLlmPromptDescription } from '../component/dialogPlan/DialogPlanLlmPromptForm';
import AppBarContext from '../context/AppBarContext';

export default function DialogPlan() {

    useUserActivityTracker(ActivityEnum.Admin, AdminSubactivityEnum.DialogPlan)

    const { setErrorMessage } = React.useContext(AppBarContext)
    const { showErrorDialog, dialog } = useErrorDialog();
    const history = useHistory();
    const location = useLocation();

    const [dialogPlanList, setDialogPlanList] = React.useState<IDialogPlan[]>([]);
    const [selectedDialogPlan, setSelectedDialogPlan] = React.useState<IDialogPlan | null>(null);

    const [dialogPlanLlmPromptList, setDialogPlanLlmPromptList] = React.useState<DialogPlanLlmPrompt[]>([]);
    const [selectedDialogPlanLlmPrompt, setSelectedDialogPlanLlmPrompt] = React.useState<DialogPlanLlmPrompt | null>(null);

    const [dialogPlanIsAdding, setDialogPlanIsAdding] = React.useState(false);
    const [dialogPlanLlmPromptIsAdding, setDialogPlanLlmPromptIsAdding] = React.useState(false);

    React.useEffect(() => {
        UserApi.assertAdmin()
            .then(response => {
                if (response.status < 300) {
                    return response.data
                }
                console.error(response)
                handleErrorMessage(response, setErrorMessage, history, location);
                return []
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })

        DialogPlanApi.getAll()
            .then(response => {
                if (response.status < 300) {
                    return response.data;
                }
                throw new Error("Unable to DialogPlanApi.getAll() " + response.status)
            })
            .then(list => setDialogPlanList(list))
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
        DialogPlanLlmPromptApi.getAll()
            .then(response => {
                if (response.status < 300) {
                    return response.data;
                }
                throw new Error("Unable to DialogPlanLlmPromptApi.getAll() " + response.status)
            })
            .then(list => setDialogPlanLlmPromptList(list))
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }, [history, location, setErrorMessage]);

    /*
        DIALOGPLAN HANDLERS
     */

    const handleExpandDialogPlan = (dialogPlanId: number) => {
        setDialogPlanList((prevDialogPlanList) =>
            prevDialogPlanList.map((dialogPlan) =>
                dialogPlan.id === dialogPlanId ? { ...dialogPlan, expanded: !dialogPlan.expanded } : dialogPlan
            )
        );
    };

    const handleAddDialogPlan = async () => {
        setDialogPlanIsAdding(true)
        const newDialogPlan: IDialogPlan = {
            id: 0,
            name: 'New DialogPlan Name',
            dialog_title: 'new dialog title',
            dialog_subtitle: undefined,
        };
        DialogPlanApi.create(newDialogPlan)
            .then(response => {
                if (response.status < 300) {
                    return response.data
                }
                console.error(response)
                handleErrorMessage(response, setErrorMessage, history, location);
                return null
            })
            .then(newDialogPlanWithId => {
                setSelectedDialogPlan(newDialogPlanWithId)
                setSelectedDialogPlanLlmPrompt(null)
                // setSelectedDialog(null)
                // setSelectedDialogPrompt(null)
                DialogPlanApi.getAll().then(response => {
                    setDialogPlanList(response.data)
                })
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
            .finally(() => {
                setDialogPlanIsAdding(false)
            })
    };

    const handleDialogPlanMenuClick = (dialogPlan: IDialogPlan) => {
        setSelectedDialogPlan(dialogPlan)
        setSelectedDialogPlanLlmPrompt(null)
        // setSelectedDialog(null)
        // setSelectedDialogPrompt(null)
        handleExpandDialogPlan(dialogPlan.id)
    };

    const handleDialogPlanFormSubmit = async (updatedDialogPlan: IDialogPlan) => {
        DialogPlanApi.update(updatedDialogPlan)
            .then(response => {
                if (response.status < 300) {
                    return response.data
                }
                console.error(response)
                handleErrorMessage(response, setErrorMessage, history, location);
                return []
            })
            .then(() => {
                DialogPlanApi.getAll().then(response => {
                    setDialogPlanList(response.data)
                })
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
                showErrorDialog(err);
            })
    };

    const handleDeleteDialogPlan = async (dialogPlanId: number) => {
        DialogPlanApi.delete(dialogPlanId)
            .then(response => {
                if (response.status < 300) {
                    return response.data
                }
                console.error(response)
                handleErrorMessage(response, setErrorMessage, history, location);
                return []
            })
            .then(() => {
                setSelectedDialogPlan(null)
                DialogPlanApi.getAll().then(response => {
                    setDialogPlanList(response.data)
                })
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }

    /*
        DIALOG PROMPT HANDLERS
     */

    const handleAddDialogPlanLlmPrompt = async (dialogPlanId: number) => {
        setDialogPlanLlmPromptIsAdding(true)
        const largestOrderIndex = dialogPlanLlmPromptList.reduce((maxOrderIndex, dialogPrompt) => {
            return Math.max(maxOrderIndex, dialogPrompt.order_index);
        }, 0);
        const newDialogPrompt: DialogPlanLlmPrompt = {
            id: 0,
            class_name: "DialogPlanLlmPromptModel",
            dialog_plan_id: dialogPlanId,
            order_index: Math.ceil(largestOrderIndex + 1),
            is_teacher: true,
            prompt: 'New prompt',
            is_note_to_student: false,
        };
        DialogPlanLlmPromptApi.create(newDialogPrompt)
            .then(response => {
                if (response.status < 300) {
                    return response.data
                }
                console.error(response)
                handleErrorMessage(response, setErrorMessage, history, location);
                return null
            })
            .then(newDialogPromptWithId => {
                setSelectedDialogPlan(null)
                setSelectedDialogPlanLlmPrompt(newDialogPromptWithId)
                DialogPlanLlmPromptApi.getAll().then(response => {
                    setDialogPlanLlmPromptList(response.data)
                })
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
            .finally(() => {
                setDialogPlanLlmPromptIsAdding(false)
            })
    };

    const handleDialogPlanLlmPromptMenuClick = (dialogPlanLlmPrompt: DialogPlanLlmPrompt) => {
        setSelectedDialogPlan(null)
        setSelectedDialogPlanLlmPrompt(dialogPlanLlmPrompt)
        // setSelectedDialog(null)
        // setSelectedDialogPrompt(dialogPrompt)
    };

    const handleDialogPlanLlmPromptFormSubmit = async (updatedDialogPlanLlmPrompt: DialogPlanLlmPrompt, setIsSaving: React.Dispatch<React.SetStateAction<boolean>>) => {

        DialogPlanLlmPromptApi.update(updatedDialogPlanLlmPrompt)
            .then(response => {
                if (response.status < 300) {
                    return response.data
                }
                console.error(response)
                handleErrorMessage(response, setErrorMessage, history, location);
                return []
            })
            .then(() => {
                DialogPlanLlmPromptApi.getAll().then(response => {
                    setDialogPlanLlmPromptList(response.data)
                })
                setIsSaving(false)
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
                showErrorDialog(err);
            })
    };

    const handleDeleteDialogPlanLlmPrompt = async (dialogPlanLlmPromptId: number) => {
        DialogPlanLlmPromptApi.delete(dialogPlanLlmPromptId)
            .then(response => {
                if (response.status < 300) {
                    return response.data
                }
                console.error(response)
                handleErrorMessage(response, setErrorMessage, history, location);
                return []
            })
            .then(() => {
                setSelectedDialogPlanLlmPrompt(null)
                DialogPlanLlmPromptApi.getAll().then(response => {
                    setDialogPlanLlmPromptList(response.data)
                })
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }

    return (
        <Container sx={{ mx: 2, my: 2 }} maxWidth={false}>
            <Grid container justifyContent="center">
                {/* <Grid item sm={12} md={6} alignItems="center" justifyContent="center" sx={{ marginTop: 2 }}> */}
                {/* Left Grid with fixed width */}
                <Grid item sx={{ width: '480px', marginTop: 2 }}>
                    <Stack spacing={2}>
                        {dialogPlanList.map(dialogPlan => (<div key={dialogPlan.id}>
                            <Stack direction="row" alignItems="center">
                                <IconButton onClick={() => handleExpandDialogPlan(dialogPlan.id)}>
                                    {dialogPlan.expanded ?
                                        <KeyboardArrowDownRoundedIcon />
                                        : <KeyboardArrowRightRoundedIcon />}
                                </IconButton>
                                <MenuBookRoundedIcon sx={{ mr: 2, color: selectedDialogPlan && selectedDialogPlan.id === dialogPlan.id ? '#FFBE19' : 'inherit' }} />
                                <Typography onClick={() => handleDialogPlanMenuClick(dialogPlan)}>
                                    {dialogPlan.name}
                                </Typography>
                            </Stack>
                            {dialogPlan.expanded == true ? (
                                <Stack>
                                    {dialogPlanLlmPromptList
                                        .filter(dialogPlanLlmPrompt => dialogPlanLlmPrompt.dialog_plan_id === dialogPlan.id)
                                        .map(dialogPlanLlmPrompt => (<div key={dialogPlanLlmPrompt.id}>
                                            <Stack direction="row" alignItems="top" sx={{ ml: 8 }}>
                                                {/* <IconButton onClick={() => handleExpandDialogPlanLlmPrompt(dialogPlanLlmPrompt.id)}>
                                                    {dialogPlanLlmPrompt.expanded ? <KeyboardArrowDownRoundedIcon /> : <KeyboardArrowRightRoundedIcon />}
                                                </IconButton> */}
                                                <ArticleRoundedIcon sx={{ mr: 2, mt: 1, color: selectedDialogPlanLlmPrompt && selectedDialogPlanLlmPrompt.id === dialogPlanLlmPrompt.id ? '#FFBE19' : 'inherit' }} />
                                                <div>
                                                    <Typography onClick={() => handleDialogPlanLlmPromptMenuClick(dialogPlanLlmPrompt)}>
                                                        {dialogPlanLlmPrompt.prompt && dialogPlanLlmPrompt.prompt.length > 20
                                                            ? `${dialogPlanLlmPrompt.prompt.substring(0, 40)} ...`
                                                            : dialogPlanLlmPrompt.prompt && dialogPlanLlmPrompt.prompt.length > 0
                                                                ? dialogPlanLlmPrompt.prompt
                                                                : dialogPlanLlmPrompt.class_name}
                                                    </Typography>
                                                    <Typography color="primary" sx={{ fontWeight: 400, opacity: 0.5, mb: 1 }}>
                                                        {getDialogPlanLlmPromptDescription(dialogPlanLlmPrompt.class_name)}
                                                    </Typography>
                                                </div>
                                            </Stack>
                                        </div>))
                                    }
                                    <Button variant="outlined" onClick={() => handleAddDialogPlanLlmPrompt(dialogPlan.id)} disabled={dialogPlanLlmPromptIsAdding} sx={{ width: 'fit-content', mt: 2, ml: 8 }}>
                                        Add DialogPlanLlmPrompt
                                    </Button>
                                </Stack>
                            ) : null}
                        </div>))}
                        <Button variant="outlined" onClick={handleAddDialogPlan} disabled={dialogPlanIsAdding} sx={{ width: 'fit-content', ml: 4 }}>
                            Add DialogPlan
                        </Button>
                    </Stack>
                </Grid>
                {/* <Grid item sm={12} md={6} alignItems="center" justifyContent="center" component={Paper} elevation={6}> */}
                {/* Right Grid that takes the remaining space */}
                <Grid item sx={{ flexGrow: 1, alignItems: 'center', justifyContent: 'center' }} component={Paper} elevation={6}>
                    {selectedDialogPlan != null ? (
                        <DialogPlanForm dialogPlanId={selectedDialogPlan.id} onSubmit={handleDialogPlanFormSubmit} onDelete={handleDeleteDialogPlan} />
                    ) : null}
                    {selectedDialogPlanLlmPrompt != null ? (
                        <DialogPlanLlmPromptForm dialogPlanLlmPromptId={selectedDialogPlanLlmPrompt.id} onSubmit={handleDialogPlanLlmPromptFormSubmit} onDelete={handleDeleteDialogPlanLlmPrompt} />
                    ) : null}
                </Grid>
            </Grid>
            {/* Error dialog */}
            {dialog}
        </Container>
    );
}
